import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from './input'

/** GCC country codes with min/max local-digit counts (digits after the +code). */
export const GCC_CODES = [
  { code: '+966', label: '🇸🇦 +966', country: 'SA', min: 9, max: 9 }, // Saudi Arabia
  { code: '+971', label: '🇦🇪 +971', country: 'AE', min: 9, max: 9 }, // UAE
  { code: '+965', label: '🇰🇼 +965', country: 'KW', min: 8, max: 8 }, // Kuwait
  { code: '+973', label: '🇧🇭 +973', country: 'BH', min: 8, max: 8 }, // Bahrain
  { code: '+968', label: '🇴🇲 +968', country: 'OM', min: 8, max: 8 }, // Oman
  { code: '+974', label: '🇶🇦 +974', country: 'QA', min: 8, max: 8 }, // Qatar
] as const

export type GccCode = typeof GCC_CODES[number]['code']
export const DEFAULT_GCC_CODE: GccCode = '+966'

/** Split a stored E.164-ish phone into (country code, local digits). */
export function splitPhone(full: string | undefined | null): { code: GccCode; local: string } {
  if (!full) return { code: DEFAULT_GCC_CODE, local: '' }
  const trimmed = full.trim()
  const match = GCC_CODES.find(c => trimmed.startsWith(c.code))
  if (match) return { code: match.code, local: trimmed.slice(match.code.length).replace(/\D/g, '') }
  // Legacy / unknown — fall back to default code, strip any leading + and non-digits.
  return { code: DEFAULT_GCC_CODE, local: trimmed.replace(/\D/g, '') }
}

/** True if the full E.164-ish phone is empty (allowed) OR has a valid GCC code + local length. */
export function isValidPhone(full: string | undefined | null): boolean {
  if (!full) return true
  const match = GCC_CODES.find(c => full.startsWith(c.code))
  if (!match) return false
  const local = full.slice(match.code.length)
  if (!/^\d+$/.test(local)) return false
  return local.length >= match.min && local.length <= match.max
}

type Props = {
  /** Full phone string with country code, e.g. "+966512345678". Empty string when blank. */
  value: string
  /** Called with the full phone string (or "" if local is empty). */
  onChange: (full: string) => void
  /** Forwarded to the local-number input. */
  id?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  className?: string
  /** When true, shows a red ring + helper text if the current value is non-empty and invalid. */
  showValidation?: boolean
}

export function PhoneInput({
  value, onChange,
  id, placeholder, required, disabled, className,
  showValidation = true,
}: Props) {
  const { t } = useTranslation()
  const initial = splitPhone(value)
  const [code, setCode] = useState<GccCode>(initial.code)
  const [local, setLocal] = useState(initial.local)

  // Keep internal state in sync when the parent resets the value (e.g. after save).
  useEffect(() => {
    const next = splitPhone(value)
    setCode(next.code)
    setLocal(next.local)
  }, [value])

  const emit = (nextCode: GccCode, nextLocal: string) => {
    const digitsOnly = nextLocal.replace(/\D/g, '')
    const meta = GCC_CODES.find(c => c.code === nextCode)!
    const clamped = digitsOnly.slice(0, meta.max)
    setCode(nextCode)
    setLocal(clamped)
    onChange(clamped ? `${nextCode}${clamped}` : '')
  }

  const meta = GCC_CODES.find(c => c.code === code) ?? GCC_CODES[0]
  const invalid = showValidation && local.length > 0 && (local.length < meta.min || local.length > meta.max)

  return (
    <div className={className}>
      {/* Force LTR regardless of page direction — a phone number's country
          code + digits must read in a fixed order (code, then number); in an
          RTL layout the flex row would otherwise visually reverse them. */}
      <div className="flex gap-2" dir="ltr">
        <select
          value={code}
          onChange={e => emit(e.target.value as GccCode, local)}
          disabled={disabled}
          aria-label={t('phoneInput.countryCode', 'Country code')}
          // Same fill as the Input beside it — a country selector that sits on the
          // page plane while its phone field is white reads as two unrelated
          // controls glued together.
          className="h-9 rounded-md border border-input bg-[hsl(var(--input-bg,var(--background)))] px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring shrink-0 disabled:opacity-50"
        >
          {GCC_CODES.map(({ code, label }) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </select>
        <Input
          id={id}
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          value={local}
          onChange={e => emit(code, e.target.value)}
          placeholder={placeholder ?? (meta.min === 9 ? '5x xxx xxxx' : '5xxx xxxx')}
          required={required}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={`flex-1 ${invalid ? 'border-destructive focus-visible:ring-destructive' : ''}`}
          maxLength={meta.max}
        />
      </div>
      {invalid && (
        <p className="text-xs text-destructive mt-1">
          {t('phoneInput.digitsRequired', {
            defaultValue: '{{country}} numbers need {{count}} digits after {{code}}.',
            country: meta.country,
            count: meta.min === meta.max ? String(meta.min) : `${meta.min}–${meta.max}`,
            code,
          })}
        </p>
      )}
    </div>
  )
}
